import type { TableActionGroup } from "@shared/types/data-table"
import type { Subscription } from "@/features/subscriptions/types/subscriptions/subscription.types"

export interface SubscriptionActionHandlers {
  onViewSubscription?: (subscription: Subscription) => void
  onUpdateSubscription?: (subscription: Subscription) => void
  onPausePaymentCollections?: (subscription: Subscription) => void
  onSharePaymentUpdateLink?: (subscription: Subscription) => void
  onCreateOneTimeInvoice?: (subscription: Subscription) => void
  onCancelSubscription?: (subscription: Subscription) => void
  onViewPlan?: (subscription: Subscription) => void
  onViewCustomer?: (subscription: Subscription) => void
  onBulkCancel?: (subscriptionIds: string[]) => void
  onBulkExport?: (subscriptionIds: string[]) => void
}

// Row actions for tables - quick access operations
export const getSubscriptionRowActions = (handlers: SubscriptionActionHandlers = {}): TableActionGroup<Subscription>[] => [
  {
    label: "ACTIONS",
    actions: [
      {
        key: "updateSubscription",
        label: "Update subscription",
        onClick: (subscription: Subscription) => handlers.onUpdateSubscription?.(subscription),
      },
      {
        key: "pausePaymentCollections",
        label: "Pause payment collections",
        onClick: (subscription: Subscription) => handlers.onPausePaymentCollections?.(subscription),
      },
      {
        key: "sharePaymentUpdateLink",
        label: "Share payment update link",
        onClick: (subscription: Subscription) => handlers.onSharePaymentUpdateLink?.(subscription),
      },
      {
        key: "createOneTimeInvoice",
        label: "Create one time invoice",
        onClick: (subscription: Subscription) => handlers.onCreateOneTimeInvoice?.(subscription),
      },
      {
        key: "cancelSubscription",
        label: "Cancel subscription",
        onClick: (subscription: Subscription) => handlers.onCancelSubscription?.(subscription),
        variant: "destructive" as const,
      },
    ]
  },
  {
    label: "CONNECTIONS",
    actions: [
      {
        key: "viewSubscription",
        label: "View subscription",
        onClick: (subscription: Subscription) => handlers.onViewSubscription?.(subscription),
      },
      {
        key: "viewPlan",
        label: "View plan",
        onClick: (subscription: Subscription) => handlers.onViewPlan?.(subscription),
      },
      {
        key: "viewCustomer",
        label: "View customer",
        onClick: (subscription: Subscription) => handlers.onViewCustomer?.(subscription),
      },
    ]
  }
]

// Panel actions for detail pages - comprehensive operations
export const getSubscriptionPanelActions = (handlers: SubscriptionActionHandlers = {}): TableActionGroup<Subscription>[] => [
  {
    label: "ACTIONS",
    actions: [
      {
        key: "updateSubscription",
        label: "Update subscription",
        onClick: (subscription: Subscription) => handlers.onUpdateSubscription?.(subscription),
      },
      {
        key: "pausePaymentCollections",
        label: "Pause payment collections",
        onClick: (subscription: Subscription) => handlers.onPausePaymentCollections?.(subscription),
      },
      {
        key: "sharePaymentUpdateLink",
        label: "Share payment update link",
        onClick: (subscription: Subscription) => handlers.onSharePaymentUpdateLink?.(subscription),
      },
      {
        key: "createOneTimeInvoice",
        label: "Create one time invoice",
        onClick: (subscription: Subscription) => handlers.onCreateOneTimeInvoice?.(subscription),
      },
      {
        key: "cancelSubscription",
        label: "Cancel subscription",
        onClick: (subscription: Subscription) => handlers.onCancelSubscription?.(subscription),
        condition: (subscription: Subscription) => subscription.status === 'active' || subscription.status === 'scheduled',
        variant: "destructive" as const,
      },
    ]
  }
]

export const getSubscriptionBulkActions = (handlers: SubscriptionActionHandlers = {}) => [
  {
    key: "export",
    label: "Export",
    onClick: (ids: string[]) => handlers.onBulkExport?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
  {
    key: "cancel",
    label: "Cancel",
    onClick: (ids: string[]) => handlers.onBulkCancel?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
]

export const getSubscriptionHoverActions = (handlers: SubscriptionActionHandlers = {}) => [
  {
    key: "viewSubscription",
    label: "View subscription",
    onClick: (subscription: Subscription) => handlers.onViewSubscription?.(subscription),
  },
]
